import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { useNavState } from '@/providers/NavStateProvider';
import { useEffect, useState } from 'react';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { Link, useNavigate } from 'react-router-dom';
import { useFixedAssets } from '@/hooks/api/2000_fixed_assets';
import { FixedAsset, FixedAssetStatus, PaymentTaskFixedAsset, SalesOrder } from '@/hooks/api/models';
import { dateFormat } from '@/utils';
import { Pagination } from '@/components/element/Pagination';
import { MotionDiv } from '@/components/element/MotionDiv';
import { DataTable } from 'primereact/datatable';
import { FilterMatchMode } from 'primereact/api';
import { useDataTableSortContent } from '@/hooks/useDataTableSortContent';
import { Column } from 'primereact/column';

export const FixedAssets = () => {
  const { setTitle, setBreadcrumbs } = useNavState();
  useEffect(() => setTitle('固定資産管理'), []);
  useEffect(() => setBreadcrumbs([]), []);

  const { _w_, me } = useCurrentWorkspace();

  return (
    <WorkspaceLayout>
      <div className="container-fluid py-4">
        <div className="d-flex align-items-center mb-4">
          <a
            href={`${process.env.REACT_APP_API_URL}/api/workspaces/${_w_!.identifier}/fixed_assets/export_csv.csv`}
            className="btn btn-icon btn-dark me-3"
          >
            <i className="fas fa-file-download"></i> Export
          </a>
        </div>

        <MotionDiv className="card mb-4">
          <div className="card-header d-flex align-items-center justify-content-between">
            <div>
              <h5 className="mb-0">固定資産一覧</h5>
            </div>
            {me!.admin && (
              <Link to={`/w/${_w_!.identifier}/finance/fixed_assets/new`} className="btn bg-gradient-primary btn-icon">
                <i className="fas fa-plus"></i>新規固定資産登録
              </Link>
            )}
          </div>

          <FixedAssetsElement />
        </MotionDiv>
      </div>
    </WorkspaceLayout>
  );
};

export interface FixedAssetData {
  id: number;
  status: string;
  name: string;
  quantity: number;
  start_date: string;
  image_file_url: string;
  total_amount: number;
}

const FixedAssetsElement = () => {
  const navigate = useNavigate();
  const { _w_ } = useCurrentWorkspace();
  const [page, setPage] = useState(1);
  const [faList, setFaList] = useState<FixedAssetData[]>();
  const { data } = useFixedAssets({ identifier: _w_!.identifier, page: page, per: 1000 });

  const [filters, setFilters] = useState({
    global: { value: '', matchMode: FilterMatchMode.CONTAINS },
  });

  const nameTemplate = (fa: FixedAssetData) => (
    <div className="d-flex align-items-center h6 mb-0">
      <div className="avatar avatar-lg me-2">
        <img src={fa.image_file_url} alt="profile_image" className="w-70 border-radius-lg shadow-sm" />
      </div>
      <span className="text-xs">{fa.name}</span>
    </div>
  );

  const statusTemplate = (fa: FixedAssetData) => {
    if (fa.status == 'preparing') return <span className="badge badge-info">構築中</span>;

    if (fa.status == 'started') return <span className="badge badge-success">使用開始済</span>;

    return <></>;
  };

  const totalAmountTemplate = (fa: FixedAssetData) => `¥${fa.total_amount.toLocaleString()}`;

  // DataTableのソート、キーワード検索
  const { dataTableSortContent, onSort, onKeywordChanged } = useDataTableSortContent(`fixed_assets_${_w_!.id}`);

  // 検索ワードの設定
  useEffect(() => {
    let _filters = { ...filters };
    _filters.global.value = dataTableSortContent.dataTableSearchKeyword?.keyword || '';
    setFilters(_filters);
  }, [dataTableSortContent]);

  useEffect(() => {
    if (data) {
      setFaList(
        data.fixed_assets.map((fa) => {
          return {
            id: fa.id,
            status: fa.fixed_asset_status.name,
            name: fa.name,
            quantity: fa.quantity,
            start_date: dateFormat({ date: fa.start_date }),
            image_file_url: fa.image_file_url,
            total_amount: fa.payment_task_fixed_assets.reduce((sum, a) => sum + a.amount, 0),
          };
        })
      );
    }
  }, [data]);

  if (data == null || faList == null) return <></>;

  if (faList.length == 0)
    return (
      <div className="card-body">
        <p className="card-description text-sm">登録されている固定資産がありません。</p>
      </div>
    );

  const onPageLinkClick = (_page: number) => setPage(_page);

  return (
    <>
      <div className="card-header mt-0 pt-0 pb-0 d-flex justify-content-between">
        <div></div>
        <div>
          <div className="input-group">
            <span className="input-group-text">
              <i className="fas fa-search"></i>
            </span>
            <input
              className="form-control"
              placeholder="Search"
              type="text"
              value={filters.global.value}
              onChange={(e) => {
                const value = e.target.value;
                let _filters = { ...filters };
                _filters.global.value = value;
                setFilters(_filters);
                onKeywordChanged(value);
              }}
            />
          </div>
        </div>
      </div>

      <div className="card-body">
        <div className="table-responsive">
          <DataTable
            value={faList}
            stripedRows
            scrollable
            scrollHeight="800px"
            tableClassName="table data-table align-items-center clickable-row"
            removableSort
            onRowClick={(e) => {
              navigate(`/w/${_w_!.identifier}/finance/fixed_assets/${e.data.id}`);
            }}
            filters={filters}
            globalFilterFields={['name', 'fixed_asset_status', 'start_date']}
            emptyMessage="No Data."
            onSort={onSort}
            sortField={dataTableSortContent.dataTableSortField?.filed}
            sortOrder={dataTableSortContent.dataTableSortField?.sortOrder}
          >
            <Column
              header="固定資産名"
              field="name"
              body={nameTemplate}
              bodyClassName="text-xxs px-2 font-weight-bold"
              headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
              frozen
              sortable
            ></Column>
            <Column
              header="ステータス"
              field="status"
              body={statusTemplate}
              bodyClassName="text-xs px-2 font-weight-bold"
              headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
              frozen
              sortable
            ></Column>
            <Column
              header="使用開始日"
              field="start_date"
              bodyClassName="text-xxs px-2 font-weight-bold"
              headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
              frozen
              sortable
            ></Column>
            <Column
              header="合計金額"
              field="total_amount"
              body={totalAmountTemplate}
              bodyClassName="text-xxs px-6 font-weight-bold text-end"
              headerClassName="text-uppercase text-xxs font-weight-bolder text-end px-6"
              style={{ width: '60px' }}
              frozen
              sortable
            ></Column>
            <Column
              header="数量"
              field="quantity"
              bodyClassName="text-xxs px-2 font-weight-bold text-end"
              headerClassName="text-uppercase text-xxs font-weight-bolder text-end px-2"
              style={{ width: '60px' }}
              frozen
              sortable
            ></Column>
          </DataTable>
        </div>
      </div>
    </>
  );
};

const FixedAssetRow = ({ fa }: { fa: FixedAsset }) => {
  const navigate = useNavigate();
  const { _w_ } = useCurrentWorkspace();

  return (
    <tr onClick={() => navigate(`/w/${_w_!.identifier}/finance/fixed_assets/${fa.id}`)}>
      <td className="text-xs px-2">
        <div className="d-flex align-items-center h6">
          <div className="avatar avatar-lg me-2">
            <img src={fa.image_file_url} alt="profile_image" className="w-100 border-radius-lg shadow-sm" />
          </div>
          {fa.name}
        </div>
      </td>
      <td className="text-xs px-2 font-weight-bold">
        <FixedAssetStatusBadge status={fa.fixed_asset_status} />
      </td>
      <td className="text-xs px-2 font-weight-bold">{dateFormat({ date: fa.start_date })}</td>
      <td className="text-xs px-2 font-weight-bold text-end">
        ¥{fa.payment_task_fixed_assets.reduce((sum, a) => sum + a.amount, 0).toLocaleString()}
      </td>
      <td className="text-xs px-2 font-weight-bold text-end">{fa.quantity}</td>
    </tr>
  );
};
const FixedAssetStatusBadge = ({ status }: { status: FixedAssetStatus }) => {
  if (status.name == 'preparing') return <span className="badge badge-info">構築中</span>;

  if (status.name == 'started') return <span className="badge badge-success">使用開始済</span>;

  return <></>;
};
